/** Where selling or contact happens — dimension on events/parties, not a DB entity. */
export const SALES_CHANNELS = ['ECOMMERCE', 'SHOWROOM', 'LANDING'] as const;
export type SalesChannel = (typeof SALES_CHANNELS)[number];

/**
 * Channels used on orders, reservations, and stock movements.
 * Domain mirror: `@wapharma/domain` `Channel` enum (must stay in sync).
 */
export const ORDER_SALES_CHANNELS = ['ECOMMERCE', 'SHOWROOM'] as const;
export type OrderSalesChannel = (typeof ORDER_SALES_CHANNELS)[number];

/** How a party row was created (provenance). */
export const PARTY_CREATED_VIA = [
  'SIGNUP',
  'BACK_OFFICE',
  'LEAD_CONVERSION',
  'SHOWROOM_STAFF',
  'FUNNEL',
] as const;
export type PartyCreatedVia = (typeof PARTY_CREATED_VIA)[number];

/** Intended outcome when converting a Lead. */
export const LEAD_CONVERSION_TARGETS = ['CUSTOMER', 'PARTNER'] as const;
export type LeadConversionTarget = (typeof LEAD_CONVERSION_TARGETS)[number];

export const CONVERTED_ENTITY_TYPES = ['user_profile', 'supplier'] as const;
export type ConvertedEntityType = (typeof CONVERTED_ENTITY_TYPES)[number];

export const SUPPLIER_KINDS = ['SUPPLIER', 'PARTNER'] as const;
export type SupplierKind = (typeof SUPPLIER_KINDS)[number];

export function isSalesChannel(value: string): value is SalesChannel {
  return (SALES_CHANNELS as readonly string[]).includes(value);
}

export function isOrderSalesChannel(value: string): value is OrderSalesChannel {
  return (ORDER_SALES_CHANNELS as readonly string[]).includes(value);
}

export function isPartyCreatedVia(value: string): value is PartyCreatedVia {
  return (PARTY_CREATED_VIA as readonly string[]).includes(value);
}

export function isLeadConversionTarget(value: string): value is LeadConversionTarget {
  return (LEAD_CONVERSION_TARGETS as readonly string[]).includes(value);
}

/** Map legacy UserProfile.channelOrigin → split fields. */
export function parseLegacyChannelOrigin(origin: string | null | undefined): {
  sourceChannel: SalesChannel | null;
  createdVia: PartyCreatedVia | null;
} {
  const raw = origin?.trim();
  if (!raw) return { sourceChannel: null, createdVia: null };
  if (raw === 'STOREFRONT') {
    return { sourceChannel: 'ECOMMERCE', createdVia: 'SIGNUP' };
  }
  if (raw === 'BACK_OFFICE') {
    return { sourceChannel: 'ECOMMERCE', createdVia: 'BACK_OFFICE' };
  }
  if (raw.startsWith('LEAD_')) {
    return { sourceChannel: 'ECOMMERCE', createdVia: 'LEAD_CONVERSION' };
  }
  return { sourceChannel: null, createdVia: null };
}

export function salesChannelLabel(channel: SalesChannel | string | null | undefined): string {
  switch (channel) {
    case 'ECOMMERCE':
      return 'E-commerce';
    case 'SHOWROOM':
      return 'Showroom';
    case 'LANDING':
      return 'Landing';
    default:
      return channel?.trim() || '—';
  }
}

export function partyCreatedViaLabel(via: PartyCreatedVia | string | null | undefined): string {
  switch (via) {
    case 'SIGNUP':
      return 'Inscription';
    case 'BACK_OFFICE':
      return 'Back-office';
    case 'LEAD_CONVERSION':
      return 'Conversion lead';
    case 'SHOWROOM_STAFF':
      return 'Showroom';
    case 'FUNNEL':
      return 'Funnel';
    default:
      return via?.trim() || '—';
  }
}

export function leadConversionTargetToEntityType(
  target: LeadConversionTarget,
): ConvertedEntityType {
  switch (target) {
    case 'CUSTOMER':
      return 'user_profile';
    case 'PARTNER':
      return 'supplier';
  }
}
